import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import DataSync from "lib/api/DataSync";
import Client from "lib/api/Client";
import { Button, ButtonGroup, TextInput, Dropdown } from "components/common";
import Modal from "components/common/Modal";
import CloudUsersModal, { CLOUD_TABLE_STYLES, CLOUD_ICON_BTN } from "components/cloud-users/CloudUsersModal";
import { parseCloudType, parseCloudLogins } from "lib/utils/license";
import { TrashIcon } from "@heroicons/react/24/outline";

const CONTACT_TYPE_LABELS = {
  primary_poc: "Primary POC",
  billing_poc: "Billing POC",
  migration_poc: "Migration POC",
  migration_tech_poc: "Migration Tech POC",
};

const EMPTY_CONTACT = { first_name: "", last_name: "", email: "", phone: "", contact_type: "primary_poc" };
const PROVISIONING_STATUS_COLORS = {
  enabled: "#10b981",
  disabled: "#f59e0b",
  deleted: "#ef4444",
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const ONE_DAY_MS    = 24 * 60 * 60 * 1000;
const THREE_DAYS_MS = 72 * 60 * 60 * 1000;

const PILL_STYLE = {
  padding: "3px 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const DERIVED_BADGE_COLORS = {
  initStarted:      { bg: "#fff7ed", color: "#c2410c" },
  initComplete:     { bg: "#f0fdf4", color: "#15803d" },
  finalizeComplete: { bg: "#eff6ff", color: "#1d4ed8" },
  finalizeFailed:   { bg: "#fef2f2", color: "#991b1b" },
  missingContacts:  { bg: "#fdf4ff", color: "#7e22ce" },
  stalled:          { bg: "#fee2e2", color: "#dc2626" },
};

function DerivedStatusPills({ record, contacts, cloudUsers }) {
  if (!record) return null;

  const contactCount = contacts.length;
  const cloudUserCount = cloudUsers.length;
  const dataToSync = parseFloat(record.total_data_to_sync_mb || 0);
  const dataSynced = parseFloat(record.total_data_synced_mb || 0);

  const initComplete =
    contactCount > 0 &&
    cloudUserCount > 0 &&
    dataToSync > 0 &&
    dataSynced > 0 &&
    dataSynced >= dataToSync;

  const finalizeFailed =
    (record.failed_files != null && Number(record.failed_files) > 0) ||
    (record.finalized_started_at && !record.finalized_at && record.finalization_status !== "waiting");

  const finalizeComplete =
    !finalizeFailed &&
    record.finalization_status &&
    record.finalization_status !== "none" &&
    record.finalized_at &&
    new Date(record.finalized_at) < new Date();

  const now = Date.now();

  let stalled = false;
  if (record.provisioning_status !== "deleted") {
    // Stage 1: Invitation Sent → Init. Complete (7 days)
    if (!initComplete && record.created_at && now - new Date(record.created_at).getTime() > SEVEN_DAYS_MS) {
      stalled = true;
    }
    // Stage 2: Init. Complete → Finalize Started (7 days, using last_activity_at as proxy)
    if (!stalled && initComplete && !record.finalized_started_at && record.last_activity_at &&
        now - new Date(record.last_activity_at).getTime() > SEVEN_DAYS_MS) {
      stalled = true;
    }
    // Stage 3: Finalize Started → Finalize Complete (7 days)
    if (!stalled && record.finalized_started_at && !record.finalized_at &&
        now - new Date(record.finalized_started_at).getTime() > SEVEN_DAYS_MS) {
      stalled = true;
    }
    // Stage 4: Finalize Complete → Customer Live (24h M–Th, 72h F–Su)
    if (!stalled && finalizeComplete) {
      const isFriToSun = (date) => { const d = new Date(date).getDay(); return d === 0 || d === 5 || d === 6; };
      const threshold = isFriToSun(record.finalized_at) ? THREE_DAYS_MS : ONE_DAY_MS;
      if (now - new Date(record.finalized_at).getTime() > threshold) stalled = true;
    }
  }

  const missingContacts = contactCount === 0;
  const initStarted =
    !initComplete &&
    dataToSync > 0 &&
    cloudUserCount > 0 &&
    contactCount > 0;

  const pills = [];
  if (initStarted)      pills.push({ key: "initStarted",      label: "Init. Started" });
  if (initComplete)     pills.push({ key: "initComplete",     label: "Init. Complete" });
  if (finalizeComplete) pills.push({ key: "finalizeComplete", label: "Finalize Complete" });
  if (finalizeFailed)   pills.push({ key: "finalizeFailed",   label: "Finalize Failed" });
  if (missingContacts)  pills.push({ key: "missingContacts",  label: "Missing Contacts" });
  if (stalled)          pills.push({ key: "stalled",          label: "Stalled" });

  if (pills.length === 0) return null;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
      {pills.map(({ key, label }) => (
        <span key={key} style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS[key].bg, color: DERIVED_BADGE_COLORS[key].color }}>
          {label}
        </span>
      ))}
    </div>
  );
}

const SYNC_STATUS_COLORS = {
  SUCCESS: "#10b981",
  ERROR: "#ef4444",
  RUNNING: "#3b82f6",
  UNKNOWN: "#6b7280",
};

export default function DataSyncDetail(props) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showCloudProvisionedModal, setShowCloudProvisionedModal] = useState(false);
  const [showCustomerLiveModal, setShowCustomerLiveModal] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [contactsSaving, setContactsSaving] = useState(false);
  const [contactsSaved, setContactsSaved] = useState(false);

  const [cloudUsers, setCloudUsers] = useState([]);
  const [showCloudUsersModal, setShowCloudUsersModal] = useState(false);
  const [cloudUsersLoading, setCloudUsersLoading] = useState(false);

  const [failedFiles, setFailedFiles] = useState([]);
  const [showFailedFilesModal, setShowFailedFilesModal] = useState(false);
  const [failedFilesLoading, setFailedFilesLoading] = useState(false);
  const [failedFilesError, setFailedFilesError] = useState(null);

  const clientUuid = props.match.params.client_uuid;

  async function load() {
    setLoading(true);
    try {
      const response = await DataSync.load(clientUuid);
      if (response.data) {
        setRecord(response.data);
        loadContacts(response.data);
      } else {
        setMessage({ type: "error", text: "Data sync record not found." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to load record." });
    }
    setLoading(false);
  }

  async function handleToggleStatus() {
    if (!record) return;
    const newStatus = record.provisioning_status === "enabled" ? "disabled" : "enabled";
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.updateStatus(clientUuid, newStatus);
      if (response.status === 200) {
        setRecord((prev) => ({ ...prev, provisioning_status: newStatus }));
        setMessage({ type: "success", text: `Status updated to ${newStatus}.` });
      } else {
        setMessage({ type: "error", text: "Failed to update status." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to update status." });
    }
    setSaving(false);
  }

  async function handleResetFinalization() {
    if (!window.confirm("This will clear all finalization data for this client and allow the customer to access ComputerEase and restart CE Live in their on premise environment. Are you sure?")) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.resetFinalization(clientUuid);
      if (response.status === 200) {
        setMessage({ type: "success", text: "Finalization reset successfully." });
        load();
        if (
          (record.iam_user_name || record.iam_access_key_id) &&
          window.confirm(
            "Would you like to reset the AWS access associated with this client in order to allow/enable access again?\n\n" +
              "A client that completes finalization loses partial AWS write access. Resetting will remove and re-enable the IAM user."
          )
        ) {
          await handleResetAwsAccess();
        }
      } else {
        setMessage({ type: "error", text: response.message || "Failed to reset finalization." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to reset finalization." });
    }
    setSaving(false);
  }

  async function handleResetAwsAccess() {
    setSaving(true);
    setMessage(null);
    try {
      const removeResponse = await DataSync.remove(clientUuid);
      if (removeResponse.status !== 200) {
        setMessage({ type: "error", text: removeResponse.message || "Failed to remove AWS access." });
        setSaving(false);
        return;
      }
      const enableResponse = await DataSync.updateStatus(clientUuid, "enabled");
      if (enableResponse.status === 200) {
        setMessage({ type: "success", text: "AWS access reset and re-enabled successfully." });
        load();
      } else {
        setMessage({ type: "error", text: enableResponse.message || "AWS access removed but failed to re-enable." });
        load();
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to reset AWS access." });
    }
    setSaving(false);
  }

  async function handleMarkCloudProvisioned() {
    setShowCloudProvisionedModal(false);
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.markCloudProvisioned(clientUuid);
      if (response.status === 200) {
        setMessage({
          type: "success",
          text: `Cloud provisioned email sent to ${response.recipients} recipient${response.recipients !== 1 ? "s" : ""}.`,
        });
        load();
      } else {
        setMessage({ type: "error", text: response.message || "Failed to send cloud provisioned email." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to send cloud provisioned email." });
    }
    setSaving(false);
  }

  async function handleMarkCustomerLive() {
    setShowCustomerLiveModal(false);
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.markCustomerLive(clientUuid);
      if (response.status === 200) {
        setMessage({
          type: "success",
          text: `Customer live email sent to ${response.recipients} recipient${response.recipients !== 1 ? "s" : ""}.`,
        });
        load();
      } else {
        setMessage({ type: "error", text: response.message || "Failed to send customer live email." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to send customer live email." });
    }
    setSaving(false);
  }

  async function handleDeleteFiles() {
    if (!window.confirm("This will permanently delete all synced files for this client. Are you sure?")) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.deleteFiles(clientUuid);
      if (response.status === 200) {
        setMessage({ type: "success", text: "Files deleted successfully." });
        load();
      } else {
        setMessage({ type: "error", text: response.message || "Failed to delete files." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to delete files." });
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!window.confirm("This will permanently delete the IAM user and all associated keys. Are you sure?")) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await DataSync.remove(clientUuid);
      if (response.status === 200) {
        setRecord((prev) => ({ ...prev, provisioning_status: "deleted" }));
        setMessage({ type: "success", text: "Data sync configuration deleted and IAM user removed." });
      } else {
        setMessage({ type: "error", text: response.message || "Failed to delete." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to delete." });
    }
    setSaving(false);
  }

  function loadContacts(data) {
    const src = data || record;
    setContacts((src?.client?.settings?.contacts || []).map(({ is_primary, ...rest }) => rest));
  }

  function addContact() {
    setContacts([...contacts, { ...newContact }]);
    setNewContact(EMPTY_CONTACT);
    setShowContactForm(false);
  }

  function removeContact(index) {
    setContacts(contacts.filter((_, i) => i !== index));
  }

  async function saveContacts() {
    setContactsSaving(true);
    try {
      await Client.updateContacts(clientUuid, contacts.map(({ is_primary, ...rest }) => rest));
      setContactsSaved(true);
      setTimeout(() => setContactsSaved(false), 3000);
    } catch (e) {
      console.error(e);
    }
    setContactsSaving(false);
  }

  async function loadCloudUsers() {
    setCloudUsersLoading(true);
    try {
      const response = await Client.listCloudUsers(clientUuid);
      setCloudUsers(response.data || []);
    } catch (e) {
      console.error(e);
    }
    setCloudUsersLoading(false);
  }

  async function loadFailedFiles() {
    setFailedFilesLoading(true);
    setFailedFilesError(null);
    try {
      const response = await DataSync.getFailedFiles(clientUuid);
      if (response.data) {
        setFailedFiles(response.data);
      } else {
        setFailedFilesError(response.message || "Failed to load failed files.");
      }
    } catch (e) {
      console.error(e);
      setFailedFilesError("Failed to load failed files.");
    }
    setFailedFilesLoading(false);
  }


  useEffect(() => {
    load();
    loadCloudUsers();
  }, [clientUuid]);

  if (loading) {
    return (
      <PageLayout>
        <h1>Cloud Migration Detail</h1>
        <div className="hr hr--darkest"></div>
        <p>Loading...</p>
      </PageLayout>
    );
  }

  if (!record) {
    return (
      <PageLayout>
        <h1>Cloud Migration Detail</h1>
        <div className="hr hr--darkest"></div>
        <p>Record not found.</p>
        <ButtonGroup>
          <Link className="button" to="/data-syncs">
            Back to List
          </Link>
        </ButtonGroup>
      </PageLayout>
    );
  }

  const isDeleted = record.provisioning_status === "deleted";
  const syncColor = SYNC_STATUS_COLORS[record.sync_status] || "#6b7280";
  const provisionColor = PROVISIONING_STATUS_COLORS[record.provisioning_status] || "#6b7280";

  const cloudLoginLimit = parseCloudLogins(record.client?.permissions?.celicense);
  const cloudType = parseCloudType(record.client?.permissions?.celicense);

  const card = {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "20px 24px",
    marginBottom: 20,
  };

  const cardTitle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #f3f4f6",
  };

  const fieldLabel = {
    fontSize: 11,
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
  };

  const fieldValue = {
    fontSize: 14,
    color: "#111827",
  };

  const divider = {
    borderTop: "1px solid #f3f4f6",
    margin: "16px 0",
  };

  function Field({ label, children, mono = false, breakAll = false, col = "col-xs-12 col-sm-3" }) {
    return (
      <div className={col} style={{ marginBottom: 16 }}>
        <div style={fieldLabel}>{label}</div>
        <div style={{ ...fieldValue, fontFamily: mono ? "monospace" : undefined, fontSize: mono ? "0.88em" : undefined, wordBreak: breakAll ? "break-all" : undefined }}>
          {children || "-"}
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <h1>Cloud Migration Detail</h1>
      <div className="hr hr--darkest"></div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: 16, alignItems: "center" }}>
        <Link className="button" to="/data-syncs">
          Back to List
        </Link>
        {isDeleted ? (
          <Button onClick={handleToggleStatus} disabled={saving}>
            Enable
          </Button>
        ) : (
          <>
            <Button onClick={handleToggleStatus} disabled={saving}>
              {record.provisioning_status === "enabled" ? "Disable" : "Enable"}
            </Button>
            <Button onClick={handleDelete} disabled={saving || (!record.iam_user_name && !record.iam_access_key_id)} style={{ backgroundColor: "#ef4444", color: "#fff" }}>
              Remove AWS Access
            </Button>
          </>
        )}
        {record.finalization_status && record.finalization_status !== "none" && (
          <Button onClick={handleResetFinalization} disabled={saving} style={{ backgroundColor: "#6366f1", color: "#fff" }}>
            Reset Finalization
          </Button>
        )}
        <Button onClick={handleDeleteFiles} disabled={saving} style={{ backgroundColor: "#f59e0b", color: "#fff" }}>
          Delete Files
        </Button>
      </div>

      {message && (
        <div style={{ marginBottom: 16, padding: "10px 15px", borderRadius: 6, backgroundColor: message.type === "error" ? "#fee2e2" : "#d1fae5", color: message.type === "error" ? "#991b1b" : "#065f46" }}>
          {message.text}
        </div>
      )}

      {/* Client */}
      <div style={card}>
        <div style={{ ...cardTitle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Client Information</span>
          {record.client_uuid && (
            <Link
              to={`/client/${record.client_uuid}`}
              style={{ fontSize: 12, padding: "3px 12px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", color: "#374151", textDecoration: "none" }}
            >
              View Customer
            </Link>
          )}
        </div>
        <div className="row">
          <Field label="Client Name" col="col-xs-12 col-sm-4">{record.client?.name}</Field>
          <Field label="Client Code" col="col-xs-12 col-sm-2">{record.client?.code}</Field>
          <Field label="Desktop Version" col="col-xs-12 col-sm-2">{record.client?.desktop_version}</Field>
          <Field label="Client UUID" col="col-xs-12 col-sm-4" mono>{record.client_uuid}</Field>
        </div>
        <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 16, paddingTop: 14 }}>
          <div style={fieldLabel}>Status</div>
          <DerivedStatusPills record={record} contacts={contacts} cloudUsers={cloudUsers} />
        </div>
        <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 16, paddingTop: 14 }}>
          <div style={fieldLabel}>Actions</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              onClick={() => setShowContactsModal(true)}
              style={{ fontSize: 12, padding: "3px 12px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer" }}
            >
              Manage {contacts.length} Contact{contacts.length !== 1 ? "s" : ""}
            </button>
            <button
              onClick={() => { setShowCloudUsersModal(true); loadCloudUsers(); }}
              style={{ fontSize: 12, padding: "3px 12px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer" }}
            >
              {cloudUsersLoading ? "Loading..." : `Manage ${cloudUsers.length} Cloud User${cloudUsers.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>

      {/* Sync Status */}
      <div style={card}>
        <div style={{ ...cardTitle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Sync Status</span>
          <button
            onClick={load}
            disabled={loading}
            style={{ fontSize: 12, padding: "2px 10px", borderRadius: 4, border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: loading ? "not-allowed" : "pointer", fontWeight: 500 }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Activity */}
        <div className="row">
          <Field label="Sync Status" col="col-xs-12 col-sm-2">
            <span style={{ fontWeight: 700, color: syncColor }}>{record.sync_status || "-"}</span>
          </Field>
          <Field label="Provisioning Status" col="col-xs-12 col-sm-2">
            <span style={{ fontWeight: 700, color: provisionColor }}>
              {record.provisioning_status
                ? record.provisioning_status.charAt(0).toUpperCase() + record.provisioning_status.slice(1)
                : "-"}
            </span>
          </Field>
          <Field label="Current Activity" col="col-xs-12 col-sm-2">{record.current_activity}</Field>
          <Field label="Created At" col="col-xs-12 col-sm-3">
            {record.created_at ? new Date(record.created_at).toLocaleString() : "-"}
          </Field>
          <Field label="Last Activity" col="col-xs-12 col-sm-3">
            {record.last_activity_at ? new Date(record.last_activity_at).toLocaleString() : "-"}
          </Field>
          {record.cleanup_completed_at && (
            <Field label="Cleanup Completed At" col="col-xs-12 col-sm-3">
              {new Date(record.cleanup_completed_at).toLocaleString()}
            </Field>
          )}
        </div>

        <div style={divider} />

        {/* Finalization */}
        <div className="row">
          <Field label="Finalization Status" col="col-xs-12 col-sm-2">{record.finalization_status}</Field>
          <Field label="Finalized Started At" col="col-xs-12 col-sm-3">
            {record.finalized_started_at ? new Date(record.finalized_started_at).toLocaleString() : "-"}
          </Field>
          <Field label="Finalized At" col="col-xs-12 col-sm-3">
            {record.finalized_at ? new Date(record.finalized_at).toLocaleString() : "-"}
          </Field>
        </div>

        <div style={divider} />

        {/* Data */}
        <div className="row">
          <Field label="Files Synced" col="col-xs-12 col-sm-2">
            {(record.files_synced || 0).toLocaleString()}
          </Field>
          {record.failed_files != null && Number(record.failed_files) > 0 && (
            <Field label="Failed Files" col="col-xs-12 col-sm-2">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#dc2626", fontWeight: 600 }}>{Number(record.failed_files).toLocaleString()}</span>
                <button
                  onClick={() => { setShowFailedFilesModal(true); loadFailedFiles(); }}
                  style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid #dc2626", background: "transparent", color: "#dc2626", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  View
                </button>
              </div>
            </Field>
          )}
          <Field label="Data Synced (MB)" col="col-xs-12 col-sm-2">
            {parseFloat(record.total_data_synced_mb || 0).toFixed(3)}
          </Field>
          <Field label="Total to Sync (MB)" col="col-xs-12 col-sm-2">
            {parseFloat(record.total_data_to_sync_mb || 0).toFixed(3)}
          </Field>
        </div>
      </div>

      {/* Cloud Lifecycle Notifications */}
      <div style={card}>
        <div style={cardTitle}>Cloud Lifecycle Notifications</div>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: -6, marginBottom: 16 }}>
          Notify the customer of migration milestones. Each action emails all active cloud users and records the date and time it was sent.
        </p>
        <div className="row">
          <div className="col-xs-12 col-sm-6" style={{ marginBottom: 16 }}>
            <div style={fieldLabel}>Cloud Provisioned</div>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 10px 0" }}>
              Cloud site is provisioned for all users.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Button onClick={() => setShowCloudProvisionedModal(true)} disabled={saving || isDeleted} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
                {record.cloud_provisioned_email_sent_at ? "Resend Email" : "Mark Cloud Provisioned"}
              </Button>
              <span style={{ fontSize: 13, color: record.cloud_provisioned_email_sent_at ? "#15803d" : "#9ca3af" }}>
                {record.cloud_provisioned_email_sent_at
                  ? `Sent ${new Date(record.cloud_provisioned_email_sent_at).toLocaleString()}`
                  : "Not sent yet"}
              </span>
            </div>
          </div>
          <div className="col-xs-12 col-sm-6" style={{ marginBottom: 16 }}>
            <div style={fieldLabel}>Customer Live</div>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 10px 0" }}>
              Customer's cloud environment has the live data.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Button
                onClick={() => setShowCustomerLiveModal(true)}
                disabled={saving || isDeleted || !record.cloud_provisioned_email_sent_at}
                style={{ backgroundColor: "#16a34a", color: "#fff" }}
              >
                {record.customer_live_email_sent_at ? "Resend Email" : "Mark Customer Live"}
              </Button>
              <span style={{ fontSize: 13, color: record.customer_live_email_sent_at ? "#15803d" : "#9ca3af" }}>
                {record.customer_live_email_sent_at
                  ? `Sent ${new Date(record.customer_live_email_sent_at).toLocaleString()}`
                  : record.cloud_provisioned_email_sent_at
                  ? "Not sent yet"
                  : "Available after Cloud Provisioned"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* AWS Configuration */}
      <div style={card}>
        <div style={cardTitle}>AWS Configuration</div>
        <div className="row">
          <Field label="IAM User Name" col="col-xs-12 col-sm-3" mono>{record.iam_user_name}</Field>
          <Field label="IAM User ARN" col="col-xs-12 col-sm-6" mono breakAll>{record.iam_user_arn}</Field>
          <Field label="AWS Region" col="col-xs-12 col-sm-2">{record.aws_region}</Field>
        </div>
        <div className="row">
          <Field label="S3 Bucket" col="col-xs-12 col-sm-3" mono>{record.bucket}</Field>
          <Field label="S3 Prefix" col="col-xs-12 col-sm-3" mono>{record.s3_prefix}</Field>
          <Field label="Access Key ID" col="col-xs-12 col-sm-3" mono>{record.iam_access_key_id}</Field>
          {record.iam_pending_key_id && (
            <Field label="Pending Key ID" col="col-xs-12 col-sm-3" mono>{record.iam_pending_key_id}</Field>
          )}
        </div>
      </div>
      <Modal show={showFailedFilesModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Failed Files</h2>
          {failedFilesLoading && <p>Loading...</p>}
          {failedFilesError && <p style={{ color: "#e53e3e" }}>{failedFilesError}</p>}
          {!failedFilesLoading && !failedFilesError && failedFiles.length === 0 && (
            <p style={{ color: "#718096" }}>No failed files found.</p>
          )}
          {!failedFilesLoading && failedFiles.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>File Path</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failedFiles.map((f, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "monospace", fontSize: "0.85em", wordBreak: "break-all" }}>{f.file_path || f.path || f.key || f.name || "-"}</td>
                      <td style={{ color: "#dc2626" }}>{f.error || f.error_message || f.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="button-group" style={{ marginTop: "20px" }}>
            <Button className="button button--plain" onClick={() => { setShowFailedFilesModal(false); setFailedFiles([]); setFailedFilesError(null); }}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <Modal show={showCloudProvisionedModal}>
        <div style={{ padding: "24px", maxWidth: "480px" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Mark Cloud Provisioned</h2>
          <p style={{ marginBottom: "24px" }}>
            This will email <strong>all active cloud users</strong> for{" "}
            {record.client?.name || "this client"} to let them know their cloud
            site is provisioned, and record the date and time it was sent. Continue?
          </p>
          <div className="button-group">
            <Button onClick={handleMarkCloudProvisioned} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
              Send Email
            </Button>
            <Button className="button button--plain" onClick={() => setShowCloudProvisionedModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal show={showCustomerLiveModal}>
        <div style={{ padding: "24px", maxWidth: "480px" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Mark Customer Live</h2>
          <p style={{ marginBottom: "24px" }}>
            This will email <strong>all active cloud users</strong> for{" "}
            {record.client?.name || "this client"} to let them know their cloud
            environment now has the live data, and record the date and time it
            was sent. Continue?
          </p>
          <div className="button-group">
            <Button onClick={handleMarkCustomerLive} style={{ backgroundColor: "#16a34a", color: "#fff" }}>
              Send Email
            </Button>
            <Button className="button button--plain" onClick={() => setShowCustomerLiveModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal show={showContactsModal} contentStyle={{ padding: 0 }}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Contacts</h2>
          {contacts.length > 0 && (
            <table style={CLOUD_TABLE_STYLES.table}>
              <thead>
                <tr>
                  <th style={CLOUD_TABLE_STYLES.th}>Contact</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Phone</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Type</th>
                  <th style={{ ...CLOUD_TABLE_STYLES.th, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, i) => (
                  <tr key={i}>
                    <td style={CLOUD_TABLE_STYLES.td}>
                      <div>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{c.email}</div>
                    </td>
                    <td style={CLOUD_TABLE_STYLES.td}>{c.phone || "—"}</td>
                    <td style={CLOUD_TABLE_STYLES.td}>{CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type}</td>
                    <td style={{ ...CLOUD_TABLE_STYLES.td, textAlign: "right" }}>
                      <button type="button" title="Remove" style={CLOUD_ICON_BTN} onClick={() => removeContact(i)}>
                        <TrashIcon style={{ width: 16, height: 16, color: "#dc2626" }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {contacts.length === 0 && !showContactForm && (
            <p style={{ color: "#718096", marginBottom: "12px" }}>No contacts on file.</p>
          )}
          {showContactForm && (
            <div style={{ background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: "4px", padding: "16px", marginTop: "8px" }}>
              <h3 style={{ marginTop: 0 }}>New Contact</h3>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <TextInput label="First Name" type="text" value={newContact.first_name}
                    onChange={e => setNewContact({ ...newContact, first_name: e.target.value })} />
                </div>
                <div className="col-xs-12 col-sm-6">
                  <TextInput label="Last Name" type="text" value={newContact.last_name}
                    onChange={e => setNewContact({ ...newContact, last_name: e.target.value })} />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <TextInput label="Email" type="email" value={newContact.email}
                    onChange={e => setNewContact({ ...newContact, email: e.target.value })} />
                </div>
                <div className="col-xs-12 col-sm-6">
                  <TextInput label="Phone (optional)" type="text" value={newContact.phone}
                    onChange={e => setNewContact({ ...newContact, phone: e.target.value })} />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <Dropdown label="Contact Type" value={newContact.contact_type}
                    onChange={e => setNewContact({ ...newContact, contact_type: e.target.value })}>
                    <option value="primary_poc">Primary POC</option>
                    <option value="billing_poc">Billing POC</option>
                    <option value="migration_poc">Migration POC</option>
                    <option value="migration_tech_poc">Migration Tech POC</option>
                  </Dropdown>
                </div>
              </div>
              <div className="button-group" style={{ marginTop: "12px" }}>
                <Button className="button" onClick={addContact}
                  disabled={!newContact.first_name || !newContact.last_name || !newContact.email}>
                  Add
                </Button>
                <Button className="button button--plain" onClick={() => { setShowContactForm(false); setNewContact(EMPTY_CONTACT); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #f3f4f6" }}>
            {!showContactForm && (
              <Button className="button button--plain" onClick={() => setShowContactForm(true)}>+ Add Contact</Button>
            )}
            <Button className="button" onClick={saveContacts} disabled={contactsSaving}>
              {contactsSaving ? "Saving..." : "Save Contacts"}
            </Button>
            <Button className="button button--plain" onClick={() => { setShowContactsModal(false); setShowContactForm(false); setNewContact(EMPTY_CONTACT); }}>
              Close
            </Button>
            {contactsSaved && <span style={{ color: "#38a169" }}>Saved!</span>}
          </div>
        </div>
      </Modal>

      <CloudUsersModal
        show={showCloudUsersModal}
        onClose={() => setShowCloudUsersModal(false)}
        clientUuid={clientUuid}
        clientName={record.client?.name}
        cloudType={cloudType}
        cloudLoginLimit={cloudLoginLimit}
        cloudUsers={cloudUsers}
        setCloudUsers={setCloudUsers}
        loading={cloudUsersLoading}
        onReload={loadCloudUsers}
      />
    </PageLayout>
  );
}
